import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { environment } from './../environments/environment';
import { SKIP_LOADER_HEADER } from './../loader.interceptor';
import { USessionService } from './usession.service';

/**
 * Records how far through a signup someone got, including the ones who never
 * finish.
 *
 * The transaction endpoint was only called once the member reached plan
 * selection, so anyone who typed their name on step 1 and closed the tab left
 * no trace at all. This posts the same endpoint from step 1 as the form is
 * filled in, which is what makes "xyz started a signup and stopped after the
 * basic details" answerable.
 *
 * ONE RECORD PER SIGNUP, NOT ONE PER KEYSTROKE. Every call carries the same
 * `reference` for the life of the browser tab, and the backend is expected to
 * upsert on (reference, process) rather than insert. Until that upsert exists
 * these calls will pile up as separate rows — see docs/signup-progress-tracking.md.
 */

/** Shared transaction endpoint — the same path both StaticApis maps call `saveplan`. */
const TRANSACTION_PATH = 'api/common/customer/transaction';

/** sessionStorage key holding the per-tab reference. */
const REFERENCE_KEY = 'signupProgressRef';

/** How long to wait after the last keystroke before posting. */
const DEBOUNCE_MS = 2000;

export interface SignupProgressMeta {
  /** Which signup produced this, e.g. 'Gig Signup form'. */
  portal: string;
  /** Step being recorded. Step 2/3 already send planSelection / enrollmentSummary. */
  process?: string;
  /** Form link, broker and so on, for the back office. */
  form?: any;
}

@Injectable({ providedIn: 'root' })
export class SignupProgressService {
  private readonly endpoint = `${environment.app.grp}${TRANSACTION_PATH}`;

  private subscription?: Subscription;
  private form?: FormGroup;
  private meta: SignupProgressMeta = { portal: '' };

  /** Last payload sent, so an unchanged form does not re-post. */
  private lastSentSnapshot = '';
  /** Latest payload built but not yet confirmed sent, used by the page-hide flush. */
  private pendingPayload: any = null;

  private pageHideHandler?: () => void;

  /**
   * These fire while the member is mid-sentence, so they must not raise the
   * global loader. The interceptor strips this header before sending.
   */
  private readonly quietHeaders = new HttpHeaders().set(SKIP_LOADER_HEADER, 'true');

  constructor(private http: HttpClient, private uSession: USessionService) {}

  /**
   * Step-1 tracking: posts the form as it is filled in.
   *
   * Opt-in, not opt-out. Until the backend upserts on (reference, process) this
   * writes a new row every couple of seconds per signup, so an environment block
   * that forgets the flag must stay silent rather than flood the table.
   */
  public get enabled(): boolean {
    return (environment.app as any)?.trackSignupProgress === true;
  }

  /**
   * Stable id for this signup attempt, held for the life of the tab.
   *
   * NOT SENT TODAY — the transactions table has no reference column, so the
   * backend matches on email + formId + process instead. Kept because that key
   * breaks if someone edits their email mid-form (it forks a second row), which
   * a reference would not. Uncomment the `reference` line in buildPayload once
   * the column exists.
   */
  public getReference(): string {
    try {
      const existing = sessionStorage.getItem(REFERENCE_KEY);
      if (existing) return existing;

      const created = `sg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(REFERENCE_KEY, created);
      return created;
    } catch {
      // Private mode with storage blocked: fall back to a per-call id.
      return `sg-${Date.now()}`;
    }
  }

  /**
   * Watches a step-1 form and posts a debounced snapshot as it is filled in.
   * Safe to call again when the form is rebuilt — the previous watch is dropped.
   */
  public startTracking(form: FormGroup, meta: SignupProgressMeta): void {
    this.stopTracking();
    if (!this.enabled || !form) return;

    this.form = form;
    this.meta = meta;

    this.subscription = form.valueChanges
      .pipe(debounceTime(DEBOUNCE_MS))
      .subscribe({
        next: () => this.send(),
        error: () => { /* tracking must never disturb the form */ },
      });

    // A closed tab is exactly the case this exists to catch, so flush on the
    // way out. visibilitychange fires on mobile where pagehide often does not.
    this.pageHideHandler = () => this.flush();
    window.addEventListener('pagehide', this.pageHideHandler);
    window.addEventListener('visibilitychange', this.pageHideHandler);
  }

  public stopTracking(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;

    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler);
      window.removeEventListener('visibilitychange', this.pageHideHandler);
      this.pageHideHandler = undefined;
    }

    this.form = undefined;
    this.pendingPayload = null;
  }

  /** Posts the current form state immediately, skipping the debounce. */
  public send(): void {
    if (!this.enabled || !this.form) return;

    const payload = this.buildPayload(this.form.value);
    if (!payload) return;

    const snapshot = JSON.stringify(payload);
    if (snapshot === this.lastSentSnapshot) return;

    this.pendingPayload = payload;
    this.lastSentSnapshot = snapshot;

    this.http.post(this.endpoint, this.uSession.attach(payload), { headers: this.quietHeaders }).subscribe({
      next: () => { this.pendingPayload = null; },
      error: (error: any) => {
        // Keep it pending so a later flush retries, and never surface this.
        console.log('[signup progress] failed', error);
      },
    });
  }

  /**
   * Last-gasp send when the tab is closing. A normal XHR is usually cancelled at
   * that point, so this goes out through sendBeacon, which the browser promises
   * to deliver after the page is gone.
   */
  public flush(): void {
    if (!this.enabled || !this.form) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;

    const payload = this.buildPayload(this.form.value) ?? this.pendingPayload;
    if (!payload) return;

    const snapshot = JSON.stringify(this.uSession.attach(payload));
    if (snapshot === this.lastSentSnapshot && !this.pendingPayload) return;
    this.lastSentSnapshot = snapshot;

    try {
      const blob = new Blob([snapshot], { type: 'application/json' });
      if (navigator.sendBeacon?.(this.endpoint, blob)) {
        this.pendingPayload = null;
        return;
      }
    } catch {
      // fall through to the normal post
    }

    this.http.post(this.endpoint, this.uSession.attach(payload), { headers: this.quietHeaders }).subscribe({
      next: () => { this.pendingPayload = null; },
      error: () => { /* the tab is going away; nothing useful left to do */ },
    });
  }

  /**
   * Builds the transaction payload, or null when there is not yet enough to be
   * worth recording. An untouched form would otherwise create an empty row for
   * every page view.
   */
  private buildPayload(value: any): any {
    if (!value) return null;

    const firstName = this.text(value.firstName);
    const lastName = this.text(value.lastName);
    const email = this.text(value.email);
    const phone = this.text(value.phone_number);

    // Email is the key the backend matches on (email + formId + process), so
    // nothing is sent until it looks like a finished address. Posting on every
    // keystroke of a half-typed address would fork a row per prefix —
    // "z@", "za@", "zaa@" — which is exactly what this is meant to avoid.
    if (!this.looksLikeEmail(email)) return null;

    return {
      customerName: `${firstName} ${lastName}`.trim(),
      contactNum: phone,
      dob: this.text(value.dob) || this.text(value.date_of_birth),
      email: email,
      process: this.meta.process ?? 'personalInformation',
      // Top level, because that is where the controller reads them from.
      formId: this.meta.form?.id ?? 1,
      link: this.meta.form?.link ?? '',
      portal: this.meta.portal,
      // Dropped for now — the backend matches on email + formId + process
      // instead. Put it back if a reference column is ever added.
      // reference: this.getReference(),
      request: {
        personalInformation: value,
        ...(this.meta.form ? { form: this.meta.form } : {}),
      },
    };
  }

  /** Good enough to tell a finished address from one still being typed. */
  private looksLikeEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }

  /** Form values can be Date objects or null; this keeps the payload as strings. */
  private text(value: any): string {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = `${value.getMonth() + 1}`.padStart(2, '0');
      const day = `${value.getDate()}`.padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return String(value).trim();
  }
}
